import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, of, forkJoin } from 'rxjs';
import { expand, map, reduce, catchError, switchMap, shareReplay } from 'rxjs/operators';
import { APP_CONFIG } from '@environments/environment';
import { ProductService } from './product.service';
import { OrganizationService } from './organization.service';
import { ProductOrganizationRoleResponseDto } from '@qc/shared/dto';

import { ResponsePaginatedWrapper } from '@/app/shared/dto';
import {
    ClientInformationRecordResponseDto,
    ProductResponseDto,
    OrganizationResponseDto,
    ContactResponseDto,
    ContactWithRoleResponseDto,
    SopResponseDto,
    EntityContactResponseDto,
    DispositionProcessConfigResponseDto,
    HcLotReleaseConfigResponseDto,
    ComplaintsProcessConfigResponseDto,
    PvProcessConfigResponseDto,
    RecallProcessConfigResponseDto,
    TempExcursionPostReleaseConfigResponseDto,
    WarehouseDetailsResponseDto,
    WarehouseProductManagementResponseDto,
    ShippingProcessConfigResponseDto,
    CreateClientInformationRecordRequestDto,
    UpdateClientInformationRecordRequestDto,
    ReturnsProcessConfigResponseDto,
    DestructionProcessConfigResponseDto,
    MedicalDeviceReleaseConfigResponseDto,
    MandatoryProblemReportingConfigResponseDto,
    CreateProductRequestDto,
    UpdateProductRequestDto,
    CreateOrganizationRequestDto,
    UpdateOrganizationRequestDto,
    CreateContactRequestDto,
    UpdateContactRequestDto,
    CreateEntityContactRequestDto,
    UpdateEntityContactRequestDto,
    CreateWarehouseDetailsRequestDto,
    UpdateWarehouseDetailsRequestDto,
    CreateWarehouseProductManagementRequestDto,
    UpdateWarehouseProductManagementRequestDto,
    DocumentApprovalResponseDto,
    CreateDocumentApprovalRequestDto,
    UpdateDocumentApprovalRequestDto,
    RegulatoryDetailsResponseDto,
    CreateRegulatoryDetailsRequestDto,
    UpdateRegulatoryDetailsRequestDto,
} from '@qc/shared';

type ClientRecord = ClientInformationRecordResponseDto;
type Product = ProductResponseDto;
type Organization = OrganizationResponseDto;
type Contact = ContactResponseDto;
type EntityContact = EntityContactResponseDto;
type SOP = SopResponseDto;
type DispositionProcessConfig = DispositionProcessConfigResponseDto;
type HCLotReleaseConfig = HcLotReleaseConfigResponseDto;
type ComplaintsProcessConfig = ComplaintsProcessConfigResponseDto;
type PVProcessConfig = PvProcessConfigResponseDto;
type RecallProcessConfig = RecallProcessConfigResponseDto;
type TempExcursionPostReleaseConfig = TempExcursionPostReleaseConfigResponseDto;
type WarehouseDetails = WarehouseDetailsResponseDto;
type WarehouseProductManagement = WarehouseProductManagementResponseDto;
type ShippingProcessConfig = ShippingProcessConfigResponseDto;
type ReturnsProcessConfig = ReturnsProcessConfigResponseDto;
type DestructionProcessConfig = DestructionProcessConfigResponseDto;
type MedicalDeviceReleaseConfig = MedicalDeviceReleaseConfigResponseDto;
type MandatoryProblemReportingConfig = MandatoryProblemReportingConfigResponseDto;
type DocumentApproval = DocumentApprovalResponseDto;
type RegulatoryDetails = RegulatoryDetailsResponseDto;

type CreateClientInformationRecordRequest = CreateClientInformationRecordRequestDto;
type UpdateClientInformationRecordRequest = UpdateClientInformationRecordRequestDto;
type ContactWithRole = ContactWithRoleResponseDto;

@Injectable({
    providedIn: 'root',
})
export class ClientRecordsService {
    private http = inject(HttpClient);
    private productService = inject(ProductService);
    private organizationService = inject(OrganizationService);
    private apiUrl = `${APP_CONFIG.apiUrl}/client-information-records`;
    private contactsApiUrl = `${APP_CONFIG.apiUrl}/contacts`;

    // Cache for individual resources
    private contactsCache = new Map<string, Observable<Contact>>();

    private fetchAllPages<T>(url: string): Observable<T[]> {
        const getPage = (pageNumber: number): Observable<ResponsePaginatedWrapper<T>> => {
            const params = new HttpParams().set('page', pageNumber.toString()).set('limit', '100');
            return this.http.get<ResponsePaginatedWrapper<T>>(url, { params });
        };

        return getPage(1).pipe(
            expand((response) => {
                if (response.meta.currentPage < response.meta.totalPages) {
                    return getPage(response.meta.currentPage + 1);
                }
                return EMPTY;
            }),
            map((response) => response.data),
            reduce((acc, data) => acc.concat(data), [] as T[]),
        );
    }

    getClientRecords(
        page: number = 1,
        limit: number = 100,
        withDeleted?: boolean,
    ): Observable<ResponsePaginatedWrapper<ClientRecord>> {
        let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());
        if (withDeleted === true) {
            params = params.set('withDeleted', 'true');
        }
        return this.http.get<ResponsePaginatedWrapper<ClientRecord>>(this.apiUrl, { params });
    }

    getAllClientRecords(): Observable<ClientRecord[]> {
        return this.fetchAllPages<ClientRecord>(this.apiUrl);
    }

    getClientRecord(id: string): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/${id}`).pipe(map((response) => response.data));
    }

    createClientRecord(record: CreateClientInformationRecordRequest): Observable<ClientRecord> {
        return this.http.post<ClientRecord>(this.apiUrl, record);
    }

    updateClientRecord(id: string, record: UpdateClientInformationRecordRequest): Observable<ClientRecord> {
        return this.http.patch<ClientRecord>(`${this.apiUrl}/${id}`, record);
    }

    bulkUpdateClientRecord(id: string, bulkData: any): Observable<ClientRecord> {
        return this.http.patch<ClientRecord>(`${this.apiUrl}/${id}/bulk-update`, bulkData);
    }

    deleteClientRecord(id: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${id}`);
    }

    checkDocumentNumberExists(documentNumber: string): Observable<boolean> {
        const params = new HttpParams().set('documentNumber', documentNumber);
        return this.http.get<any>(this.apiUrl, { params }).pipe(
            map((response) => {
                const totalCount = response.meta?.totalCount || 0;
                return totalCount > 0;
            }),
        );
    }

    getProductsForClient(recordId: string): Observable<Product[]> {
        return this.fetchAllPages<Product>(`${this.apiUrl}/${recordId}/products`);
    }

    getClientRecordsByProductId(productId: string): Observable<ClientRecord[]> {
        return this.http
            .get<ClientRecord[]>(`${this.apiUrl}/by-product/${productId}`)
            .pipe(map((response: any) => response.data || response));
    }

    getOrganizationsForClient(recordId: string): Observable<Organization[]> {
        return this.fetchAllPages<Organization>(`${this.apiUrl}/${recordId}/organizations`);
    }

    getContactsForClient(recordId: string): Observable<ContactWithRole[]> {
        return this.fetchAllPages<ContactWithRole>(`${this.apiUrl}/${recordId}/contacts`);
    }

    getContactsForOrganization(organizationId: string): Observable<any[]> {
        return this.http
            .get<any>(`${APP_CONFIG.apiUrl}/organizations/${organizationId}/contacts`)
            .pipe(map((response) => response.data || []));
    }

    getSOPsForClient(recordId: string): Observable<SOP[]> {
        return this.http
            .get<SOP[]>(`${APP_CONFIG.apiUrl}/sops`)
            .pipe(map((sops) => sops.filter((sop: any) => sop.clientRecordId === recordId)));
    }

    getDispositionProcessConfig(clientRecordId: string): Observable<DispositionProcessConfig | null> {
        return this.fetchAllPages<DispositionProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/disposition-process-config`,
        ).pipe(map((configs) => (configs.length > 0 ? configs[0] : null)));
    }

    getDispositionProcessConfigs(clientRecordId: string): Observable<DispositionProcessConfig[]> {
        return this.fetchAllPages<DispositionProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/disposition-process-config`,
        );
    }

    getComplaintsProcessConfig(clientRecordId: string): Observable<ComplaintsProcessConfig | null> {
        return this.fetchAllPages<ComplaintsProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/complaints-process-config`,
        ).pipe(map((configs) => (configs.length > 0 ? configs[0] : null)));
    }

    getPVProcessConfig(clientRecordId: string): Observable<PVProcessConfig | null> {
        return this.fetchAllPages<PVProcessConfig>(`${this.apiUrl}/${clientRecordId}/pv-process-config`).pipe(
            map((configs) => (configs.length > 0 ? configs[0] : null)),
        );
    }

    getRecallProcessConfig(clientRecordId: string): Observable<RecallProcessConfig | null> {
        return this.fetchAllPages<RecallProcessConfig>(`${this.apiUrl}/${clientRecordId}/recall-process-config`).pipe(
            map((configs) => (configs.length > 0 ? configs[0] : null)),
        );
    }

    getTempExcursionPostReleaseConfig(clientRecordId: string): Observable<TempExcursionPostReleaseConfig | null> {
        return this.fetchAllPages<TempExcursionPostReleaseConfig>(
            `${this.apiUrl}/${clientRecordId}/temp-excursion-post-release-config`,
        ).pipe(map((configs) => (configs.length > 0 ? configs[0] : null)));
    }

    getReturnsProcessConfig(clientRecordId: string): Observable<ReturnsProcessConfig | null> {
        return this.fetchAllPages<ReturnsProcessConfig>(`${this.apiUrl}/${clientRecordId}/returns-process-config`).pipe(
            map((configs) => (configs.length > 0 ? configs[0] : null)),
        );
    }

    getDestructionProcessConfig(clientRecordId: string): Observable<DestructionProcessConfig | null> {
        return this.fetchAllPages<DestructionProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/destruction-process-config`,
        ).pipe(map((configs) => (configs.length > 0 ? configs[0] : null)));
    }

    getMedicalDeviceReleaseConfigs(clientRecordId: string): Observable<MedicalDeviceReleaseConfig[]> {
        return this.fetchAllPages<MedicalDeviceReleaseConfig>(
            `${this.apiUrl}/${clientRecordId}/medical-device-release-config`,
        );
    }

    getMedicalDeviceReleaseConfig(clientRecordId: string): Observable<MedicalDeviceReleaseConfig[]> {
        return this.fetchAllPages<MedicalDeviceReleaseConfig>(
            `${this.apiUrl}/${clientRecordId}/medical-device-release-config`,
        );
    }

    getMandatoryProblemReportingConfig(clientRecordId: string): Observable<MandatoryProblemReportingConfig | null> {
        return this.fetchAllPages<MandatoryProblemReportingConfig>(
            `${this.apiUrl}/${clientRecordId}/mandatory-problem-reporting-config`,
        ).pipe(map((configs) => (configs.length > 0 ? configs[0] : null)));
    }

    getRegulatoryDetails(clientRecordId: string): Observable<RegulatoryDetails | null> {
        return this.fetchAllPages<RegulatoryDetails>(`${this.apiUrl}/${clientRecordId}/regulatory-details`).pipe(
            map((rows) => (rows.length > 0 ? rows[0] : null)),
        );
    }

    createRegulatoryDetails(clientRecordId: string, data: CreateRegulatoryDetailsRequestDto): Observable<RegulatoryDetails> {
        return this.http.post<RegulatoryDetails>(`${this.apiUrl}/${clientRecordId}/regulatory-details`, data);
    }

    updateRegulatoryDetails(
        clientRecordId: string,
        id: string,
        data: UpdateRegulatoryDetailsRequestDto,
    ): Observable<RegulatoryDetails> {
        return this.http.patch<RegulatoryDetails>(`${this.apiUrl}/${clientRecordId}/regulatory-details/${id}`, data);
    }

    deleteRegulatoryDetails(clientRecordId: string, id: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/regulatory-details/${id}`);
    }

    getHCLotReleaseConfigs(clientRecordId: string): Observable<HCLotReleaseConfig[]> {
        return this.fetchAllPages<HCLotReleaseConfig>(`${this.apiUrl}/${clientRecordId}/hc-lot-release-config`);
    }

    getWarehouseDetails(clientRecordId: string): Observable<WarehouseDetails[]> {
        return this.http.get<any>(`${this.apiUrl}/${clientRecordId}/warehouse-details`, { withCredentials: true }).pipe(
            map((response) => {
                if (response && response.data) {
                    return response.data;
                }
                if (Array.isArray(response)) {
                    return response;
                }
                return [];
            }),
            catchError((error) => {
                console.error('Error fetching warehouse details:', error);
                return of([]);
            }),
        );
    }

    createWarehouseDetails(
        clientRecordId: string,
        warehouseData: CreateWarehouseDetailsRequestDto,
    ): Observable<WarehouseDetails> {
        return this.http
            .post<any>(`${this.apiUrl}/${clientRecordId}/warehouse-details`, warehouseData)
            .pipe(map((response) => response.data || response));
    }

    updateWarehouseDetails(
        clientRecordId: string,
        warehouseId: string,
        warehouseData: UpdateWarehouseDetailsRequestDto,
    ): Observable<WarehouseDetails> {
        return this.http
            .patch<any>(`${this.apiUrl}/${clientRecordId}/warehouse-details/${warehouseId}`, warehouseData)
            .pipe(map((response) => response.data || response));
    }

    deleteWarehouseDetails(clientRecordId: string, warehouseId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/warehouse-details/${warehouseId}`);
    }

    getWarehouseProductManagement(clientRecordId: string): Observable<WarehouseProductManagement[]> {
        return this.http.get<any>(`${this.apiUrl}/${clientRecordId}/warehouse-product-management`).pipe(
            map((response) => {
                if (response && response.data) {
                    return response.data;
                }
                if (Array.isArray(response)) {
                    return response;
                }
                return [];
            }),
            catchError((error) => {
                console.error('Error fetching warehouse product management:', error);
                return of([]);
            }),
        );
    }

    createWarehouseProductManagement(
        clientRecordId: string,
        warehouseProductData: CreateWarehouseProductManagementRequestDto,
    ): Observable<WarehouseProductManagement> {
        return this.http
            .post<any>(`${this.apiUrl}/${clientRecordId}/warehouse-product-management`, warehouseProductData)
            .pipe(map((response) => response.data || response));
    }

    updateWarehouseProductManagement(
        clientRecordId: string,
        warehouseProductId: string,
        warehouseProductData: UpdateWarehouseProductManagementRequestDto,
    ): Observable<WarehouseProductManagement> {
        return this.http
            .patch<any>(
                `${this.apiUrl}/${clientRecordId}/warehouse-product-management/${warehouseProductId}`,
                warehouseProductData,
            )
            .pipe(map((response) => response.data || response));
    }

    deleteWarehouseProductManagement(clientRecordId: string, warehouseProductId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/${clientRecordId}/warehouse-product-management/${warehouseProductId}`,
        );
    }

    getShippingProcessConfigs(clientRecordId: string): Observable<ShippingProcessConfig[]> {
        return this.fetchAllPages<ShippingProcessConfig>(`${this.apiUrl}/${clientRecordId}/shipping-process-config`);
    }

    getDocumentApprovals(clientRecordId: string): Observable<DocumentApproval[]> {
        return this.fetchAllPages<DocumentApproval>(`${this.apiUrl}/${clientRecordId}/document-approvals`);
    }

    getProductOrganizationRoles(productId: string): Observable<ProductOrganizationRoleResponseDto[]> {
        return this.http
            .get<{ data: ProductOrganizationRoleResponseDto[]; meta: any }>(
                `${APP_CONFIG.apiUrl}/products/${productId}/organization-roles`,
            )
            .pipe(map((response) => response.data || []));
    }

    getProductOrganizationRolesBatch(productIds: string[]): Observable<ProductOrganizationRoleResponseDto[]> {
        return this.http
            .post<{ data: ProductOrganizationRoleResponseDto[]; meta: any }>(
                `${APP_CONFIG.apiUrl}/products/organization-roles/batch`,
                { productIds },
            )
            .pipe(map((response) => response.data || []));
    }

    addProductOrganizationRole(productId: string, roleData: any): Observable<ProductOrganizationRoleResponseDto> {
        return this.http.post<ProductOrganizationRoleResponseDto>(
            `${APP_CONFIG.apiUrl}/products/${productId}/organization-roles`,
            roleData,
        );
    }

    updateProductOrganizationRole(
        productId: string,
        roleId: string,
        roleData: any,
    ): Observable<ProductOrganizationRoleResponseDto> {
        return this.http.patch<ProductOrganizationRoleResponseDto>(
            `${APP_CONFIG.apiUrl}/products/${productId}/organization-roles/${roleId}`,
            roleData,
        );
    }

    removeProductOrganizationRole(productId: string, roleId: string): Observable<void> {
        return this.http.delete<void>(`${APP_CONFIG.apiUrl}/products/${productId}/organization-roles/${roleId}`);
    }

    getAllProducts(): Observable<Product[]> {
        return this.productService.getAllProducts();
    }

    createProduct(productData: CreateProductRequestDto): Observable<Product> {
        return this.productService.createProduct(productData);
    }

    updateProduct(id: string, productData: UpdateProductRequestDto): Observable<Product> {
        return this.productService.updateProduct(id, productData);
    }

    deleteProduct(id: string): Observable<void> {
        return this.productService.deleteProduct(id);
    }

    getAllOrganizations(): Observable<Organization[]> {
        return this.organizationService.getAllOrganizations();
    }

    getOrganization(id: string): Observable<Organization> {
        return this.organizationService.getOrganization(id);
    }

    createOrganization(organizationData: CreateOrganizationRequestDto): Observable<Organization> {
        return this.organizationService.createOrganization(organizationData);
    }

    updateOrganization(id: string, organizationData: UpdateOrganizationRequestDto): Observable<Organization> {
        return this.organizationService.updateOrganization(id, organizationData);
    }

    deleteOrganization(id: string): Observable<void> {
        return this.organizationService.deleteOrganization(id);
    }

    getAllContacts(): Observable<Contact[]> {
        return this.fetchAllPages<Contact>(this.contactsApiUrl);
    }

    searchContacts(
        searchTerm: string = '',
        page: number = 1,
        limit: number = 50,
    ): Observable<{ items: Contact[]; meta: any }> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString())
            .set('search', searchTerm);

        return this.http.get<ResponsePaginatedWrapper<Contact>>(this.contactsApiUrl, { params }).pipe(
            map((response) => ({
                items: response.data,
                meta: response.meta,
            })),
        );
    }

    getContact(id: string): Observable<Contact> {
        // Check if already cached
        if (this.contactsCache.has(id)) {
            return this.contactsCache.get(id)!;
        }

        const request$ = this.http.get<any>(`${this.contactsApiUrl}/${id}`).pipe(
            map((response) => response.data || response),
            shareReplay(1),
        );

        // Cache the observable
        this.contactsCache.set(id, request$);
        return request$;
    }

    createContact(contactData: CreateContactRequestDto): Observable<Contact> {
        return this.http.post<any>(this.contactsApiUrl, contactData).pipe(map((response) => response.data));
    }

    updateContact(id: string, contactData: UpdateContactRequestDto): Observable<Contact> {
        return this.http.patch<any>(`${this.contactsApiUrl}/${id}`, contactData).pipe(
            map((response) => response.data),
            switchMap((updatedContact) => {
                // Clear cache for this contact after update
                this.clearContactCache(id);
                return of(updatedContact);
            }),
        );
    }

    deleteContact(id: string): Observable<void> {
        return this.http.delete<void>(`${this.contactsApiUrl}/${id}`).pipe(
            switchMap((result) => {
                // Clear cache for this contact after deletion
                this.clearContactCache(id);
                return of(result);
            }),
        );
    }

    createEntityContact(entityContactData: CreateEntityContactRequestDto): Observable<EntityContact> {
        const url = `${APP_CONFIG.apiUrl}/entity-contacts`;
        return this.http.post<EntityContact>(url, entityContactData).pipe(
            map((response) => response),
            catchError((error) => {
                console.error('createEntityContact error:', error);
                throw error;
            }),
        );
    }

    updateEntityContact(id: string, entityContactData: UpdateEntityContactRequestDto): Observable<EntityContact> {
        return this.http.patch<EntityContact>(`${APP_CONFIG.apiUrl}/entity-contacts/${id}`, entityContactData);
    }

    deleteEntityContact(id: string): Observable<void> {
        return this.http.delete<void>(`${APP_CONFIG.apiUrl}/entity-contacts/${id}`);
    }

    getEntityContacts(entityId: string, entityType: string): Observable<EntityContact[]> {
        const url = `${APP_CONFIG.apiUrl}/entity-contacts?entityId=${entityId}&entityType=${entityType}`;

        return this.http.get<any>(url).pipe(
            map((response) => {
                const items = response.data || response.items || [];
                return items;
            }),
            catchError((error) => {
                console.error('Failed to get entity contacts:', error);
                return of([]);
            }),
        );
    }

    getDocumentControlContacts(clientRecordId: string): Observable<Contact[]> {
        return this.getEntityContacts(clientRecordId, 'CLIENT_RECORD').pipe(
            map((entityContacts: EntityContact[]) => {
                const filtered = entityContacts.filter((ec: EntityContact) => ec.context === 'DOCUMENT_CONTROL');
                return filtered;
            }),
            switchMap((documentControlEntityContacts: EntityContact[]) => {
                if (documentControlEntityContacts.length === 0) {
                    return of([]);
                }

                const contactIds = documentControlEntityContacts
                    .map((ec: EntityContact) => ec.contactId)
                    .filter((id: string) => id && id !== 'undefined');

                if (contactIds.length === 0) {
                    return of([]);
                }

                const contactRequests = contactIds.map((id) => this.getContact(id));
                return forkJoin(contactRequests);
            }),
            catchError((error) => {
                console.error('Error in getDocumentControlContacts:', error);
                return of([]);
            }),
        );
    }

    associateProduct(clientRecordId: string, productId: string): Observable<void> {
        return this.http.put<void>(`${this.apiUrl}/${clientRecordId}/products/${productId}`, {});
    }

    disassociateProduct(clientRecordId: string, productId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/products/${productId}`);
    }

    associateOrganization(clientRecordId: string, organizationId: string): Observable<void> {
        return this.http.put<void>(`${this.apiUrl}/${clientRecordId}/organizations/${organizationId}`, {});
    }

    disassociateOrganization(clientRecordId: string, organizationId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/organizations/${organizationId}`);
    }

    addContactToOrganization(
        organizationId: string,
        contactId: string,
        roleDescription?: string,
        context?: string,
    ): Observable<void> {
        const body = { contactId, roleDescription, context };
        return this.http.post<void>(`${APP_CONFIG.apiUrl}/organizations/${organizationId}/contacts`, body);
    }

    removeContactFromOrganization(organizationId: string, contactId: string): Observable<void> {
        return this.http.delete<void>(`${APP_CONFIG.apiUrl}/organizations/${organizationId}/contacts/${contactId}`);
    }

    updateOrganizationContactRole(
        organizationId: string,
        contactId: string,
        roleDescription?: string,
        context?: string,
    ): Observable<void> {
        const body = { roleDescription, context };
        return this.http.patch<void>(
            `${APP_CONFIG.apiUrl}/organizations/${organizationId}/contacts/${contactId}`,
            body,
        );
    }

    createRecallProcessConfig(clientRecordId: string, configData: any): Observable<RecallProcessConfig> {
        return this.http.post<RecallProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/recall-process-config`,
            configData,
        );
    }

    updateRecallProcessConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<RecallProcessConfig> {
        return this.http.patch<RecallProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/recall-process-config/${configId}`,
            configData,
        );
    }

    deleteRecallProcessConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/recall-process-config/${configId}`);
    }

    createDispositionProcessConfig(clientRecordId: string, configData: any): Observable<DispositionProcessConfig> {
        return this.http.post<DispositionProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/disposition-process-config`,
            configData,
        );
    }

    updateDispositionProcessConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<DispositionProcessConfig> {
        return this.http.patch<DispositionProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/disposition-process-config/${configId}`,
            configData,
        );
    }

    deleteDispositionProcessConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/disposition-process-config/${configId}`);
    }

    createHCLotReleaseConfig(clientRecordId: string, configData: any): Observable<HCLotReleaseConfig> {
        return this.http.post<HCLotReleaseConfig>(`${this.apiUrl}/${clientRecordId}/hc-lot-release-config`, configData);
    }

    updateHCLotReleaseConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<HCLotReleaseConfig> {
        return this.http.patch<HCLotReleaseConfig>(
            `${this.apiUrl}/${clientRecordId}/hc-lot-release-config/${configId}`,
            configData,
        );
    }

    deleteHCLotReleaseConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/hc-lot-release-config/${configId}`);
    }

    createComplaintsProcessConfig(clientRecordId: string, configData: any): Observable<ComplaintsProcessConfig> {
        return this.http.post<ComplaintsProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/complaints-process-config`,
            configData,
        );
    }

    updateComplaintsProcessConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<ComplaintsProcessConfig> {
        return this.http.patch<ComplaintsProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/complaints-process-config/${configId}`,
            configData,
        );
    }

    deleteComplaintsProcessConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/complaints-process-config/${configId}`);
    }

    createPVProcessConfig(clientRecordId: string, configData: any): Observable<PVProcessConfig> {
        return this.http.post<PVProcessConfig>(`${this.apiUrl}/${clientRecordId}/pv-process-config`, configData);
    }

    updatePVProcessConfig(clientRecordId: string, configId: string, configData: any): Observable<PVProcessConfig> {
        return this.http.patch<PVProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/pv-process-config/${configId}`,
            configData,
        );
    }

    deletePVProcessConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/pv-process-config/${configId}`);
    }

    createTempExcursionPostReleaseConfig(
        clientRecordId: string,
        configData: any,
    ): Observable<TempExcursionPostReleaseConfig> {
        return this.http.post<TempExcursionPostReleaseConfig>(
            `${this.apiUrl}/${clientRecordId}/temp-excursion-post-release-config`,
            configData,
        );
    }

    updateTempExcursionPostReleaseConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<TempExcursionPostReleaseConfig> {
        return this.http.patch<TempExcursionPostReleaseConfig>(
            `${this.apiUrl}/${clientRecordId}/temp-excursion-post-release-config/${configId}`,
            configData,
        );
    }

    deleteTempExcursionPostReleaseConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/${clientRecordId}/temp-excursion-post-release-config/${configId}`,
        );
    }

    createReturnsProcessConfig(clientRecordId: string, configData: any): Observable<ReturnsProcessConfig> {
        return this.http.post<ReturnsProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/returns-process-config`,
            configData,
        );
    }

    updateReturnsProcessConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<ReturnsProcessConfig> {
        return this.http.patch<ReturnsProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/returns-process-config/${configId}`,
            configData,
        );
    }

    deleteReturnsProcessConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/returns-process-config/${configId}`);
    }

    createDestructionProcessConfig(clientRecordId: string, configData: any): Observable<DestructionProcessConfig> {
        return this.http.post<DestructionProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/destruction-process-config`,
            configData,
        );
    }

    updateDestructionProcessConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<DestructionProcessConfig> {
        return this.http.patch<DestructionProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/destruction-process-config/${configId}`,
            configData,
        );
    }

    deleteDestructionProcessConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/destruction-process-config/${configId}`);
    }

    createMedicalDeviceReleaseConfig(clientRecordId: string, configData: any): Observable<MedicalDeviceReleaseConfig> {
        return this.http.post<MedicalDeviceReleaseConfig>(
            `${this.apiUrl}/${clientRecordId}/medical-device-release-config`,
            configData,
        );
    }

    updateMedicalDeviceReleaseConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<MedicalDeviceReleaseConfig> {
        return this.http.patch<MedicalDeviceReleaseConfig>(
            `${this.apiUrl}/${clientRecordId}/medical-device-release-config/${configId}`,
            configData,
        );
    }

    deleteMedicalDeviceReleaseConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/medical-device-release-config/${configId}`);
    }

    createMandatoryProblemReportingConfig(
        clientRecordId: string,
        configData: any,
    ): Observable<MandatoryProblemReportingConfig> {
        return this.http.post<MandatoryProblemReportingConfig>(
            `${this.apiUrl}/${clientRecordId}/mandatory-problem-reporting-config`,
            configData,
        );
    }

    updateMandatoryProblemReportingConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<MandatoryProblemReportingConfig> {
        return this.http.patch<MandatoryProblemReportingConfig>(
            `${this.apiUrl}/${clientRecordId}/mandatory-problem-reporting-config/${configId}`,
            configData,
        );
    }

    deleteMandatoryProblemReportingConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/${clientRecordId}/mandatory-problem-reporting-config/${configId}`,
        );
    }

    createShippingProcessConfig(clientRecordId: string, configData: any): Observable<ShippingProcessConfig> {
        return this.http.post<ShippingProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/shipping-process-config`,
            configData,
        );
    }

    updateShippingProcessConfig(
        clientRecordId: string,
        configId: string,
        configData: any,
    ): Observable<ShippingProcessConfig> {
        return this.http.patch<ShippingProcessConfig>(
            `${this.apiUrl}/${clientRecordId}/shipping-process-config/${configId}`,
            configData,
        );
    }

    deleteShippingProcessConfig(clientRecordId: string, configId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/shipping-process-config/${configId}`);
    }

    createDocumentApproval(
        clientRecordId: string,
        approvalData: CreateDocumentApprovalRequestDto,
    ): Observable<DocumentApproval> {
        return this.http
            .post<any>(`${this.apiUrl}/${clientRecordId}/document-approvals`, approvalData)
            .pipe(map((response) => response.data || response));
    }

    getDocumentApproval(clientRecordId: string, approvalId: string): Observable<DocumentApproval> {
        return this.http
            .get<any>(`${this.apiUrl}/${clientRecordId}/document-approvals/${approvalId}`)
            .pipe(map((response) => response.data || response));
    }

    getVersionHistory(documentNumber: string): Observable<any[]> {
        return this.http
            .get<any>(`${this.apiUrl}/${documentNumber}/versions`)
            .pipe(
                map((response) => {
                    // Handle both wrapped (with data/meta) and unwrapped API responses
                    const versionsData = response?.data || response;
                    return Array.isArray(versionsData) ? versionsData : [];
                }),
                catchError((error) => {
                    console.error('Error fetching version history:', error);
                    return of([]);
                }),
            );
    }

    getVersionChanges(recordId: string, versionMajor: number, versionMinor: number): Observable<any[]> {
        return this.http
            .get<any>(`${this.apiUrl}/${recordId}/versions/${versionMajor}/${versionMinor}/changes`)
            .pipe(
                map((response) => {
                    // Handle both wrapped (with data/meta) and unwrapped API responses
                    const changesData = response?.data || response;
                    return Array.isArray(changesData) ? changesData : [];
                }),
                catchError((error) => {
                    console.error('Error fetching version changes:', error);
                    return of([]);
                }),
            );
    }

    updateDocumentApproval(
        clientRecordId: string,
        approvalId: string,
        approvalData: UpdateDocumentApprovalRequestDto,
    ): Observable<DocumentApproval> {
        return this.http
            .patch<any>(`${this.apiUrl}/${clientRecordId}/document-approvals/${approvalId}`, approvalData)
            .pipe(map((response) => response.data || response));
    }

    deleteDocumentApproval(clientRecordId: string, approvalId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${clientRecordId}/document-approvals/${approvalId}`);
    }

    getClientStatistics(): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/statistics/overview`).pipe(map((response) => response.data));
    }

    getProduct(id: string): Observable<Product> {
        return this.productService.getProduct(id);
    }

    // Methods to clear individual resource cache
    clearContactCache(id: string): void {
        this.contactsCache.delete(id);
    }

    clearOrganizationCache(id: string): void {
        this.organizationService.clearOrganizationCache(id);
    }

    clearProductCache(id: string): void {
        this.productService.clearProductCache(id);
    }

    // Clear all individual resource caches
    clearAllResourceCaches(): void {
        this.contactsCache.clear();
        this.organizationService.clearAllOrganizationsCache();
        this.productService.clearAllProductsCache();
    }

    // Access Control Methods
    grantUserAccess(clientId: string, userId: string): Observable<{ message: string }> {
        return this.http.post<{ message: string }>(`${this.apiUrl}/${clientId}/grant-access`, { userId });
    }

    revokeUserAccess(clientId: string, userId: string): Observable<{ message: string }> {
        return this.http.delete<{ message: string }>(`${this.apiUrl}/${clientId}/revoke-access`, {
            body: { userId }
        });
    }

    getUserClientAccess(): Observable<UserClientAccess[]> {
        return this.http.get<{ data: UserClientAccess[] }>(`${this.apiUrl}/access-control`).pipe(
            map(response => response.data || [])
        );
    }
}

// Interface for User-Client Access
interface UserClientAccess {
    id: string;
    userId: string;
    clientId: string;
    userName: string;
    userEmail: string;
    userRole: string;
    clientName: string;
    clientDocumentNumber: string;
    grantedAt: string;
}
